"use client";

import React from "react";
import { ArrowClockwiseIcon } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { isCreditPresetAction } from "@/lib/agent/presetPresentation";
import LoadingSpinner from "@/components/ui/loading-spinner";
import { PresetOption } from "@/types/createAgent";
import { getPresetDisplayLabel, getPresetMeta } from "./presetMeta";

export interface CommitPresetState {
  isProcessing: boolean;
  isDisabled: boolean;
  isSelected: boolean;
}

/**
 * Tiered commit actions (documentation/CreateChatActionStyle.md): not every
 * button deserves the same weight. One filled hero carries the forward path,
 * paid edits support it as outlines, and destructive/rare exits are fenced
 * below a divider so they are reachable but never a mis-tap away.
 */
/** Count through fragments so empty support groups do not render a row. */
function countRenderableChildren(children: React.ReactNode): number {
  let count = 0;
  React.Children.forEach(children, child => {
    if (child === null || child === undefined || typeof child === "boolean") {
      return;
    }
    if (React.isValidElement(child) && child.type === React.Fragment) {
      count += countRenderableChildren(
        (child.props as { children?: React.ReactNode }).children
      );
      return;
    }
    count += 1;
  });
  return count;
}

function getCommitRank(preset: PresetOption): number {
  switch (preset.actionId) {
    case "submit_quote":
      return 0;
    case "generate_dress_front":
    case "generate_dress_back":
      return 1;
    case "start_virtual_try_on":
      return 2;
    case "buy_credits_back":
    case "buy_credits_modify":
      return 3;
    case "continue_to_back_design":
      return 4;
    case "modify_dress_front":
    case "modify_dress_back":
      return 5;
    default:
      return 5;
  }
}

export function HeroActionButton({
  preset,
  state,
  onClick,
  badgeOverride,
  className,
}: {
  preset: PresetOption;
  state: CommitPresetState;
  onClick: () => void;
  badgeOverride?: string;
  className?: string;
}) {
  const meta = getPresetMeta(preset);
  const { isProcessing, isDisabled } = state;
  const badge = badgeOverride ?? meta.badge;

  return (
    <button
      type="button"
      data-commit-tier="hero"
      disabled={isDisabled}
      onClick={() => !isDisabled && onClick()}
      className={cn(
        "flex min-h-12 w-full min-w-0 items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold transition-colors duration-150",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        !isDisabled
          ? "bg-brand-logo text-brand-logo-foreground hover:bg-brand-logo-hover"
          : "cursor-default bg-muted/50 text-muted-foreground",
        className
      )}
    >
      <span className="flex size-5 shrink-0 items-center justify-center">
        {isProcessing ? (
          // Processing implies the disabled (muted) background — a white
          // spinner would be invisible on it in both themes.
          <LoadingSpinner size="sm" color="gray" className="shrink-0" />
        ) : (
          meta.icon
        )}
      </span>
      <span className="min-w-0 text-center [overflow-wrap:anywhere]">
        {isProcessing ? "Working..." : getPresetDisplayLabel(preset)}
      </span>
      {badge && (
        <span
          className={cn(
            "shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold leading-none ring-1",
            !isDisabled
              ? // Darkened pill keeps the white 11px text readable on the
                // violet hero (white/20 washes out to ~2:1 contrast).
                "bg-black/25 text-brand-logo-foreground ring-white/20"
              : "bg-muted text-muted-foreground ring-border"
          )}
        >
          {badge}
        </span>
      )}
    </button>
  );
}

export function SupportActionButton({
  preset,
  state,
  onClick,
  badgeOverride,
  className,
}: {
  preset: PresetOption;
  state: CommitPresetState;
  onClick: () => void;
  badgeOverride?: string;
  className?: string;
}) {
  const meta = getPresetMeta(preset);
  const { isProcessing, isDisabled } = state;
  const badge =
    badgeOverride ??
    (isCreditPresetAction(preset.actionId) ? undefined : meta.badge);

  return (
    <button
      type="button"
      data-commit-tier="support"
      disabled={isDisabled}
      onClick={() => !isDisabled && onClick()}
      className={cn(
        "flex min-h-11 w-full min-w-0 items-center justify-start gap-2 rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors duration-150",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        !isDisabled
          ? "border-border bg-card text-foreground hover:border-brand-logo/40 hover:bg-muted/35"
          : "cursor-default border-border/60 bg-muted/25 text-muted-foreground",
        className
      )}
    >
      <span className="flex size-4.5 shrink-0 items-center justify-center [&>svg]:size-4">
        {isProcessing ? (
          <LoadingSpinner size="sm" color="gray" className="shrink-0" />
        ) : (
          meta.icon
        )}
      </span>
      <span className="min-w-0 flex-1 text-left break-words">
        {isProcessing ? "Working..." : getPresetDisplayLabel(preset)}
      </span>
      {badge && (
        <span className="shrink-0 rounded-full bg-success-muted px-2 py-0.5 text-[11px] font-semibold leading-none text-success ring-1 ring-success/30">
          {badge}
        </span>
      )}
    </button>
  );
}

export function ExitActionLink({
  preset,
  state,
  onClick,
  className,
}: {
  preset: PresetOption;
  state: CommitPresetState;
  onClick: () => void;
  className?: string;
}) {
  const { isProcessing, isDisabled } = state;

  return (
    <button
      type="button"
      data-commit-tier="exit"
      disabled={isDisabled}
      onClick={() => !isDisabled && onClick()}
      className={cn(
        "flex min-h-11 min-w-0 items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-semibold transition-colors duration-150",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        !isDisabled
          ? "text-muted-foreground hover:bg-error-muted hover:text-error"
          : "cursor-default text-muted-foreground/60",
        className
      )}
    >
      {isProcessing ? (
        <LoadingSpinner size="xs" color="gray" className="shrink-0" />
      ) : (
        <ArrowClockwiseIcon size={13} weight="bold" aria-hidden="true" />
      )}
      <span className="min-w-0 truncate">{getPresetDisplayLabel(preset)}</span>
    </button>
  );
}

export function CommitActionStack({
  presets,
  getPresetState,
  onPresetClick,
  /** Extra support-tier buttons (e.g. front-review panel launchers) rendered
   * after the preset-based support actions. */
  supportChildren,
  getBadge,
  className,
  presetClassName,
}: {
  presets: PresetOption[];
  getPresetState: (preset: PresetOption) => CommitPresetState;
  onPresetClick: (preset: PresetOption) => void;
  supportChildren?: React.ReactNode;
  getBadge?: (preset: PresetOption) => string | undefined;
  className?: string;
  presetClassName?: string | ((preset: PresetOption) => string | undefined);
}) {
  const getPresetClassName = (preset: PresetOption) =>
    typeof presetClassName === "function"
      ? presetClassName(preset)
      : presetClassName;
  const exitPresets = presets.filter(
    preset => preset.actionId === "start_over"
  );
  const forwardPresets = presets
    .filter(preset => preset.actionId !== "start_over")
    .sort((a, b) => getCommitRank(a) - getCommitRank(b));
  const heroPreset = forwardPresets[0];
  const supportPresets = forwardPresets.slice(1);
  const supportChildCount = countRenderableChildren(supportChildren);
  const supportCount = supportPresets.length + supportChildCount;
  const supportCostsCredits = supportPresets.some(preset =>
    isCreditPresetAction(preset.actionId)
  );
  const showFootRow = exitPresets.length > 0 || supportCostsCredits;

  if (!heroPreset && supportCount === 0 && exitPresets.length === 0) {
    return null;
  }

  return (
    <div
      data-commit-action-stack
      className={cn("grid min-w-0 gap-2", className)}
    >
      {heroPreset && (
        <HeroActionButton
          preset={heroPreset}
          state={getPresetState(heroPreset)}
          onClick={() => onPresetClick(heroPreset)}
          badgeOverride={getBadge?.(heroPreset)}
          className={getPresetClassName(heroPreset)}
        />
      )}
      {supportCount > 0 && (
        <div className="grid min-w-0 grid-cols-1 gap-2">
          {supportPresets.map((preset, index) => (
            <SupportActionButton
              key={`${preset.actionId}-${preset.label}-${index}`}
              preset={preset}
              state={getPresetState(preset)}
              onClick={() => onPresetClick(preset)}
              badgeOverride={getBadge?.(preset)}
              className={getPresetClassName(preset)}
            />
          ))}
          {supportChildren}
        </div>
      )}
      {showFootRow && (
        <div className="mt-0.5 flex min-w-0 items-center justify-between gap-2 border-t border-border/70 pt-2">
          <span className="min-w-0 text-xs font-medium text-muted-foreground">
            {supportCostsCredits ? "Each updated preview uses 1 credit" : ""}
          </span>
          {exitPresets.map((preset, index) => (
            <ExitActionLink
              key={`${preset.actionId}-${preset.label}-${index}`}
              preset={preset}
              state={getPresetState(preset)}
              onClick={() => onPresetClick(preset)}
              className={getPresetClassName(preset)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
